import BaseComponent from "./base_component_controller";

import { useChartConfig } from "hb/insights/use_chart_config";
import { DEFAULT_QUERY } from "hb/insights/query";
import { useOutlets } from "hb/insights/use_outlets";

import hljs from "highlight.js/lib/core";

export default class extends BaseComponent {
  static viewId = "table";
  static targets = [
    "eventResults",
    "eventWithIdTemplate",
    "error",
    "eventTemplate",
    "eventList",
    "eventHeaders",
    "headerTemplate",
    "errorMessageTemplate",
    "emptyQueryTemplate",
  ];

  static values = {
    querySessionId: String,
    schema: Array,
    fields: Array,
    results: Array,
  };

  connect() {
    super.connect();

    useOutlets(this, {
      components: ["handsfree"],
      scope: this.element.closest(".handsfree-parent"),
    });

    this.eventWithIdTemplate = this.compileElement(
      this.eventWithIdTemplateTarget,
    );
    this.eventTemplate = this.compileElement(this.eventTemplateTarget);
    this.headerTemplate = this.compileElement(this.headerTemplateTarget);
    this.gridColumns = [];

    useChartConfig(this);

    this.initQuery();
  }

  colReset({ params: { colIdx } }) {
    this.gridColumns[colIdx - 1] = "auto";
    this.updateGrid();
  }

  colDrag({ pageX, params: { colIdx }, target }) {
    const parentNode = target.parentNode;
    const prevNode = parentNode.previousElementSibling;
    const startWidth = parseInt(getComputedStyle(prevNode).width, 10);

    target.classList.add("dragging");

    const resizeFn = (evt) => {
      const newWidth = startWidth + (evt.pageX - pageX);
      this.gridColumns[colIdx - 1] = `${newWidth}px`;
      this.updateGrid();
    };

    const stopFn = () => {
      target.classList.remove("dragging");
      document.removeEventListener("mousemove", resizeFn);
      document.removeEventListener("mouseup", stopFn);
    };

    document.addEventListener("mousemove", resizeFn);
    document.addEventListener("mouseup", stopFn);
  }

  colCellClick(evt) {
    if (!window.getSelection().isCollapsed) {
      return;
    }

    const field = evt.target.closest(".field");
    const { fieldType, fieldName, fieldValue } = field.dataset;

    if (fieldType === "json") {
      evt.target.closest(".event-content").classList.toggle("expanded");
    } else {
      this.showHandsfreeDropdown(fieldType, fieldName, fieldValue);
    }
  }

  showHandsfreeDropdown(type, name, value) {
    this.outletComponent("handsfree").show(type, name, value, {
      element: this.element,
      excludeTypeFromField: true,
    });
  }

  updateGrid() {
    this.eventResultsTarget.style.gridTemplateColumns =
      this.gridColumns.join(" ");
  }

  toggleMeta(evt) {
    this.eventListTarget
      .querySelector(`#event-${evt.params.eventId}`)
      .classList.toggle("expanded");
  }

  onQueryReady(evt) {
    super.onQueryReady(evt);

    const {
      detail: { query },
    } = evt;
    this.query = query.data.response.query;
  }

  onQueryEmpty() {
    this.hasEventListTarget && this.eventListTarget.classList.add("hidden");
    this.hasEventResultsTarget &&
      this.eventResultsTarget.classList.add("hidden");

    const colorQuery = hljs.highlight(DEFAULT_QUERY, {
      language: "badgerql",
    }).value;
    this.errorTarget.classList.add("show");
    const emptyTemplate = this.compileElement(this.emptyQueryTemplateTarget);
    this.errorTarget.innerHTML = emptyTemplate({ query: colorQuery });
  }

  _render() {
    if (this.errorMessage !== null) {
      this.hasEventResultsTarget &&
        this.eventResultsTarget.classList.add("hidden");
      this.hasEventListTarget && this.eventListTarget.classList.add("hidden");
      this.errorTarget.classList.add("show");
      const errorMessageTemplate = this.compileElement(
        this.errorMessageTemplateTarget,
      );
      this.errorTarget.innerHTML = errorMessageTemplate({
        message: this.errorMessage,
        showConfigMessage: this.hideConfigMessage,
      });
    } else {
      this.errorTarget.classList.remove("show");
      this.hasEventResultsTarget &&
        this.eventResultsTarget.classList.remove("hidden");
      this.hasEventListTarget &&
        this.eventListTarget.classList.remove("hidden");
    }

    super._render();
  }

  render() {
    super.connect();

    const {
      query: { meta, results },
    } = this;

    this.eventListTarget.replaceChildren();
    this.eventHeadersTarget.replaceChildren();

    if (results?.length > 0) {
      this.gridColumns = meta.fields.map(() => "auto");
      let withId = false;

      if (meta.has("@id")) {
        withId = true;
        this.element.classList.add("with-id");
        this.gridColumns.unshift("40px");
        this.eventHeadersTarget.append(document.createElement("div"));
      } else {
        this.element.classList.remove("with-id");
      }

      // Add empty header if there are no returned fields
      if (!meta.hasFields) {
        const header = document.createElement("div");
        header.innerHTML = this.headerTemplate({
          field: "",
          allowsDragging: false,
          index: 1,
        });
        this.eventHeadersTarget.append(header);
      } else {
        meta.fields.forEach((field, index) => {
          const header = document.createElement("div");
          header.innerHTML = this.headerTemplate({
            field,
            allowsDragging: index > 0,
            index: withId ? index + 1 : index,
            type: meta.shortType(field),
          });
          this.eventHeadersTarget.append(header);
        });
      }

      this.updateGrid();

      if (!meta.hasFields) {
        results.forEach(() => {
          const item = document.createElement("div");
          item.classList.add("event-content");
          this.eventListTarget.append(item);
        });
      } else {
        results.forEach((result) => {
          const item = document.createElement("div");
          item.classList.add("event-content");

          if (meta.has("@id") && !result.field("@fill")) {
            item.id = `event-${result.field("@id")}`;
            item.innerHTML = this.eventWithIdTemplate({
              id: result.field("@id"),
              ts: result.tsUTC(),
              org_id: result.field("@org.id"),
              stream_id: result.field("@stream.id"),
            });
          } else {
            item.innerHTML = this.eventTemplate({
              withFill: meta.has("@id") && result.field("@fill"),
            });
          }

          const fields = item.querySelector(".event-fields");

          meta.fields.forEach((name) => {
            const div = document.createElement("div");
            const span = document.createElement("span");
            div.addEventListener("click", this.colCellClick.bind(this));
            div.appendChild(span);

            const field = result.field(name);
            const type = meta.type(name);

            div.dataset.fieldType = type;
            if (field !== null) {
              div.dataset.fieldValue = field;
            }
            div.dataset.fieldName = name;

            div.classList.add("field", `field-type-${type}`);
            if (type === "json") {
              div.classList.add("actionable");
              span.innerHTML = hljs.highlight(field, {
                language: "json",
              }).value;
            } else {
              span.textContent = result.field(name);
            }

            if (type.startsWith("tzdatetime") || type === "datetime") {
              div.addEventListener("mouseenter", () => {
                window.dispatchEvent(
                  new CustomEvent("chart:crosshairHighlight", {
                    detail: { timestamp: field },
                  }),
                );
              });
              div.addEventListener("mouseleave", () => {
                window.dispatchEvent(new Event("chart:clearCrosshair"));
              });
            }

            fields.append(div);
          });

          this.eventListTarget.append(item);
        });
      }
    }
  }
}
